import React, { useCallback, useEffect, useState } from 'react';
import { Button, Form, Spinner } from 'react-bootstrap';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';
import { fetchVatMetadata, mappingOptions, shortLabelsByBox } from '../../../../services/api/vatMetadataApi';
import SettingsSelect from '../../Settings/SettingsSelect';
import { vatDetectionStatus } from '../../../../utils/vatDetectionState';
import VatAskAILink from './VatAskAILink';

/**
 * Explain assessed VAT treatment: supported confirmations, missing facts,
 * incompatible mappings, and folded automatic/separate-report information.
 *
 * Spec: docs/vat-compliance/VAT_UNMAPPED_ACCOUNT_DETECTION.md §4. The panel is
 * visible from the wizard at any time; the gate is on locking, not on looking,
 * so this never hides the report. Established automatic treatments have no
 * field picker; manual decisions are an advanced path for unresolved items.
 *
 * The evidence class is shown rather than hidden behind a confidence score. A
 * proposal the ledger corroborates and one the chart states outright are
 * different kinds of claim, and the user is the one who has to stand behind the
 * filing.
 *
 * UX_PLAN.md P7: the fields are named, not just numbered ("fält 05 —
 * Momspliktig försäljning …"), and "another field" is chosen from the
 * momsdeklaration's own register rather than typed as a code. The register
 * comes from the backend (G16), so this panel and the return can never
 * disagree about what a field is called.
 */

const EVIDENCE_LABEL = {
    strong: 'Enligt kontoplanen',
    corroborated: 'Stöds av bokföringen',
};
const EVIDENCE_TITLE = {
    strong: 'Kontoplanen anger fältet för det här kontonumret.',
    corroborated: 'Kontot är bokfört tillsammans med utgående moms som stämmer med fältet.',
};

const PANEL_STYLE = {
    border: '1px solid #fcd34d',
    background: '#fffbeb',
    borderRadius: '0.625rem',
    padding: '0.9rem 1rem',
};

/**
 * The three choices for one account, shared by every surface that records the
 * decision (the wizard, the import result card, Kontoplanshälsa).
 *
 * @param {object} props
 * @param {object} props.account `{ account_id, account_number, proposal }`
 * @param {boolean} props.busy
 * @param {Array} props.fieldOptions `[{ value, label }]` from the register
 * @param {object} props.shortLabels `{ '05': 'Momspliktig försäljning …' }`
 * @param {Function} props.onAccept `(vatBox)` — the proposal
 * @param {Function} props.onChoose `(vatBox)` — another field
 * @param {Function} props.onExclude
 */
export function VatTreatmentExplanation({ treatment, proposal, period }) {
    if (!treatment && !proposal?.explanation) return null;
    return (
        <details className="small mt-2 text-muted">
            <summary>{proposal ? 'Varför detta förslag?' : 'Varför denna momsbehandling?'}</summary>
            {treatment?.managed_vat?.locked && <p className="my-2 fw-semibold">Hanteras av LedgerLoop · Ingår inte i momsdeklarationen</p>}
            <p className="my-2">{treatment?.explanation || proposal.explanation}</p>
            {period && <p>Kontrollerat intervall: {period.start_date} – {period.end_date}. Nettorörelse är inte samma sak som skatt att betala.</p>}
            {treatment?.evidence?.length > 0 && <p>Underlag: {treatment.evidence.map((e) => `${e.label} (${e.date})`).join(', ')}.</p>}
            {treatment?.sources?.map((source) => <a className="d-block" key={source.id} href={source.reference} target="_blank" rel="noopener noreferrer">
                {source.id.startsWith('bfn') ? 'Bokföringsnämnden' : source.id.startsWith('bas') ? 'BAS' : 'Skatteverket'} — läs källan
            </a>)}
            {treatment && <small>Regel {treatment.rule_id}, version {treatment.rule_version}. Kontrolleras på nytt när bokföringen ändras.</small>}
        </details>
    );
}

export function VatDecisionControls({ account, busy, fieldOptions, shortLabels, onAccept, onChoose, onExclude, onClear, reviewContext }) {
    const [chosen, setChosen] = useState('');
    const managed = account.managed_vat || account.treatment?.managed_vat;
    const proposal = managed?.locked ? null : account.proposal || null;
    const conflict = account.treatment?.review === 'mapping_conflict';
    // The manual choice sits behind "Annat fält…" while there is a proposal;
    // without one it is the only way forward, so it is open from the start.
    const [manualOpen, setManualOpen] = useState(!proposal);
    const fieldName = (box) => (shortLabels && shortLabels[box] ? `fält ${box} — ${shortLabels[box]}` : `fält ${box}`);
    // Compact controls: the card already carries the account, the amount and
    // the facts, so the decisions are one short row, not a stack of blocks
    // (owner, 2026-09-06).
    const compactButton = { padding: '0.375rem 0.875rem', fontSize: '0.8125rem', lineHeight: 1.4 };
    const linkButton = { padding: 0, fontSize: '0.8125rem', textDecoration: 'none' };
    // A hairline between the three choices so they read as separate options.
    const separator = { width: 1, alignSelf: 'stretch', background: '#e5e7eb' };

    return (
        <>
            {conflict && <p className="small mt-2">{account.treatment.explanation}</p>}
            {managed?.locked && <p className="small mt-2 fw-semibold">Hanteras av LedgerLoop · Momsinställningen kan inte ändras.</p>}
            {!managed?.locked && !proposal && !conflict && <p className="small mt-2">{account.question || 'Vad avser bokningarna på kontot? Utan den uppgiften kan vi inte avgöra om ett momsrapportsfält behövs.'}</p>}
            {conflict && account.treatment.destination !== 'unknown' && account.current_vat_box && onClear && <Button size="sm" variant="outline-primary" className="mt-2" style={compactButton} disabled={busy} onClick={onClear}>
                Ta bort kopplingen till fält {account.current_vat_box} och kontrollera igen
            </Button>}
            {conflict && <p className="small text-muted mt-2">En rättelse ändrar inte redan låsta deklarationer. {managed?.locked
                ? 'Kontots momsinställning hanteras fortsatt av LedgerLoop. Om bokningen eller en granskad fördelning har fel innebörd behöver själva underlaget rättas.'
                : 'En automatisk bedömning skapar inget permanent undantag.'}</p>}
            {/* Information first: the proposal as a labelled line, with its
                "why" and the AI link beside it. Then the decisions on their own
                line, with air between them (owner, 2026-09-06: not crammed,
                just smooth). */}
            {proposal && (
                <div className="d-flex flex-wrap align-items-baseline mt-2" style={{ gap: '0.5rem 0.75rem', fontSize: '0.875rem' }}>
                    <span style={{
                        background: 'var(--rgba-primary-1, #f4f8f5)', border: '1px solid var(--rgba-primary-3, #cfe3d3)', color: 'var(--primary)',
                        borderRadius: '999px', padding: '0.05rem 0.55rem', fontSize: '0.75rem', fontWeight: 600, whiteSpace: 'nowrap',
                    }}>Förslag</span>
                    <span>{fieldName(proposal.vat_box)}</span>
                </div>
            )}
            <div className="d-flex flex-wrap align-items-baseline" style={{ gap: '0.25rem 1rem' }}>
                <VatTreatmentExplanation treatment={account.treatment} proposal={proposal} period={account.period} />
                {reviewContext && <VatAskAILink accountId={account.account_id} accountNumber={account.account_number} {...reviewContext} />}
            </div>
            {!managed?.locked && (
                <div className="d-flex flex-wrap align-items-center mt-3 pt-3" style={{ gap: '0.5rem 0.875rem', borderTop: '1px solid #f1f5f9' }}>
                    {proposal && (
                        <>
                            <Button
                                size="sm"
                                variant="primary"
                                style={compactButton}
                                disabled={busy}
                                aria-label={`Bekräfta fält ${proposal.vat_box}`}
                                onClick={() => onAccept(proposal.vat_box)}
                            >
                                {busy ? <Spinner animation="border" size="sm" /> : 'Bekräfta förslaget'}
                            </Button>
                            <span aria-hidden="true" style={separator} />
                            <Button
                                variant="link"
                                size="sm"
                                className="text-muted"
                                style={linkButton}
                                disabled={busy}
                                aria-expanded={manualOpen}
                                onClick={() => setManualOpen((open) => !open)}
                            >
                                Annat fält…
                            </Button>
                            <span aria-hidden="true" style={separator} />
                        </>
                    )}
                    <Button
                        variant="link"
                        size="sm"
                        className="text-muted"
                        style={linkButton}
                        disabled={busy}
                        onClick={onExclude}
                    >
                        Ingår inte i momsdeklarationen
                    </Button>
                </div>
            )}
            {!managed?.locked && manualOpen && (
                <div className="mt-2">
                    <div className="d-flex gap-1 align-items-center" style={{ minWidth: 260 }}>
                        {fieldOptions && fieldOptions.length > 0 ? (
                            <div style={{ flex: '1 1 260px', maxWidth: 420 }}>
                                <SettingsSelect
                                    options={fieldOptions}
                                    value={chosen}
                                    onChange={(value) => setChosen(value || '')}
                                    placeholder={proposal ? 'Annat fält…' : 'Välj fält…'}
                                    aria-label={`Momsrapportkod för konto ${account.account_number}`}
                                    isDisabled={busy}
                                />
                            </div>
                        ) : (
                            // The register could not be fetched: fall back to the
                            // code, so a decision is still possible.
                            <Form.Control
                                size="sm"
                                style={{ width: '5.5rem' }}
                                placeholder="Fält"
                                value={chosen}
                                disabled={busy}
                                aria-label={`Momsrapportkod för konto ${account.account_number}`}
                                onChange={(e) => setChosen(e.target.value.trim())}
                            />
                        )}
                        <Button
                            size="sm"
                            variant="outline-secondary"
                            style={compactButton}
                            disabled={busy || !chosen}
                            onClick={() => onChoose(chosen)}
                        >
                            Använd
                        </Button>
                    </div>
                    <p className="small text-muted mt-2 mb-0">Ett manuellt val gäller kontot, även kommande bokningar. Blandad användning behöver granskas per händelse.</p>
                </div>
            )}
        </>
    );
}

/** The register, once per mount; a failed fetch leaves the code fallback in place. */
export function useVatFieldRegister(companyId) {
    const [register, setRegister] = useState({ fieldOptions: [], shortLabels: {} });
    useEffect(() => {
        let cancelled = false;
        fetchVatMetadata(companyId)
            .then((metadata) => {
                if (cancelled) return;
                setRegister({
                    fieldOptions: mappingOptions(metadata, { includeEmpty: false }),
                    shortLabels: shortLabelsByBox(metadata),
                });
            })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [companyId]);
    return register;
}

/**
 * @param {object} props
 * @param {string} props.companyId
 * @param {string} [props.vatPeriodId] the wizard case — detection and resolve
 *   are period-scoped endpoints
 * @param {object} [props.source] the SIE import / Kontoplanshälsa case: an
 *   already computed detection plus the resolve endpoint to post to, with
 *   extra body fields (the period's range). `{ accounts, resolveUrl, resolveBody }`
 * @param {Function} [props.onResolved] called with the recomputed detection
 * @param {string} [props.title]
 * @param {string} [props.intro] replaces the default "belopp i perioden" sentence
 * @param {boolean} [props.plain] no yellow frame — the host already draws one
 */
export default function UnmappedAccountsPanel({
    companyId, vatPeriodId, source = null, onResolved, title, intro, plain = false,
}) {
    const [state, setState] = useState(source
        ? { loading: false, ...source, accounts: source.accounts || [] }
        : { loading: true, accounts: [] });
    const [busyAccount, setBusyAccount] = useState(null);
    const { fieldOptions, shortLabels } = useVatFieldRegister(companyId);

    const load = useCallback(async () => {
        if (!source && !vatPeriodId) return;
        try {
            const response = await axios.get(
                source
                    ? source.resolveUrl.replace(/\/resolve$/, '')
                    : getApiUrl(`${companyId}/reporting/vat-period/${vatPeriodId}/unmapped-accounts`),
                { withCredentials: true, ...(source ? { params: source.resolveBody } : {}) }
            );
            // "Ran and found nothing" and "could not run" are different
            // answers (independent review 2026-09-05, F10): the second is shown
            // as such, with a retry, because the backend refuses to lock the
            // report until the check has run.
            setState({
                loading: false,
                accounts: response.data.accounts || [],
                automatic: response.data.automatic || [],
                period: response.data.period,
                external_settlements: response.data.external_settlements || [],
                unavailable: vatDetectionStatus(response.data) === 'unavailable',
            });
            if (source && onResolved) onResolved(response.data);
        } catch (error) {
            // Never fatal to the wizard: a detector that cannot run must not
            // take the report down with it — but it is said, not hidden.
            setState({ loading: false, accounts: [], failed: true });
        }
    }, [companyId, vatPeriodId, source, onResolved]);

    useEffect(() => { if (!source) load(); }, [load, source]);

    const resolve = async (account, body, successText) => {
        setBusyAccount(account.account_id);
        try {
            const response = await axios.post(
                source
                    ? source.resolveUrl
                    : getApiUrl(`${companyId}/reporting/vat-period/${vatPeriodId}/unmapped-accounts/resolve`),
                { account_id: account.account_id, ...(source?.resolveBody || {}), ...body },
                { withCredentials: true }
            );
            setState({
                loading: false,
                accounts: response.data.detection?.accounts || [],
                automatic: response.data.detection?.automatic || [],
                period: response.data.detection?.period,
                external_settlements: response.data.detection?.external_settlements || [],
                unavailable: vatDetectionStatus(response.data.detection) === 'unavailable',
            });
            toast.success('Klart', { description: successText });
            // The recomputed detection travels with the callback so a parent
            // that counts what is left (the import result's to-do card) never
            // shows a stale number.
            if (onResolved) onResolved(response.data.detection || null);
        } catch (error) {
            toast.error('Fel', {
                description: error.response?.data?.message || 'Kontot kunde inte uppdateras.'
            });
        } finally {
            setBusyAccount(null);
        }
    };

    const frame = plain || (state.accounts.length === 0 && !state.external_settlements?.length)
        ? { className: 'mb-3' } : { className: 'mb-3', style: PANEL_STYLE };

    if (state.loading) return null;
    if (state.failed || state.unavailable) {
        return (
            <div className="mb-3" style={PANEL_STYLE}>
                <div className="d-flex align-items-start">
                    <AlertTriangle size={18} className="me-2 mt-1 flex-shrink-0" style={{ color: '#b45309' }} />
                    <div>
                        <div className="fw-semibold">Momskontrollen kunde inte köras</div>
                        <div className="small" style={{ color: '#78350f' }}>
                            Kontrollen av momsrapportkoder kunde inte slutföras. Momsrapporten kan inte låsas
                            förrän kontrollen har körts.
                        </div>
                        <Button
                            size="sm"
                            variant="outline-secondary"
                            className="mt-2"
                            onClick={() => { setState({ loading: true, accounts: [] }); load(); }}
                        >
                            <RefreshCw size={13} className="me-1" />
                            Försök igen
                        </Button>
                    </div>
                </div>
            </div>
        );
    }
    if (state.accounts.length === 0 && !state.automatic?.length && !state.external_settlements?.length) return null;

    return (
        <div {...frame}>
            {state.external_settlements?.length > 0 && <div className="mb-3 p-2 border rounded" role="alert">
                <strong>Momsavstämning behöver kontrolleras</strong>
                <p className="small mb-0">{state.external_settlements.map((v) => `${v.label} (${v.date})`).join(', ')} kan ha nollställt momskontona.
                    {' '}Har perioden redan deklarerats? Stäng den då som redan rapporterad med de faktiskt deklarerade beloppen.
                    Annars behöver bokningen utredas innan rapporten kan låsas. Vi tar inte bort bokningen automatiskt.</p>
            </div>}
            {!plain && state.accounts.length > 0 && (
                <div className="d-flex align-items-start mb-2">
                    <AlertTriangle size={18} className="me-2 mt-1 flex-shrink-0" style={{ color: '#b45309' }} />
                    <div>
                        <div className="fw-semibold">{title || 'Behöver ditt svar inför momsdeklarationen'}</div>
                        <div className="small" style={{ color: '#78350f' }}>
                            {intro || (state.accounts.length === 1
                                ? 'Bekräfta momsbehandlingen för ett konto. Ett bokfört belopp behöver inte vara ett momsunderlag.'
                                : `Bekräfta momsbehandlingen för ${state.accounts.length} konton. Ett bokfört belopp behöver inte vara ett momsunderlag.`)}
                        </div>
                    </div>
                </div>
            )}

            {state.accounts.map((account) => (
                <div
                    key={account.account_id}
                    className="mt-2 p-2"
                    style={{ background: '#fff', border: '1px solid #fde68a', borderRadius: '0.5rem' }}
                >
                    <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                        <div>
                            <div className="fw-semibold" style={{ fontSize: '0.9rem' }}>
                                {account.account_number} {account.account_name}
                            </div>
                            <div className="small text-muted">
                                {new Intl.NumberFormat('sv-SE', { maximumFractionDigits: 2 })
                                    .format(account.net_amount)} kr nettorörelse
                                {state.period ? ` (${state.period.start_date} – ${state.period.end_date})` : ' i perioden'}
                                {' · '}{account.posting_count} poster
                            </div>
                        </div>
                        {account.proposal && (
                            <span
                                className="small"
                                title={EVIDENCE_TITLE[account.proposal.evidence] || undefined}
                                style={{
                                    background: 'var(--rgba-primary-1, #f4f8f5)',
                                    border: '1px solid var(--rgba-primary-3, #cfe3d3)',
                                    color: 'var(--primary)',
                                    borderRadius: '999px',
                                    padding: '0.15rem 0.6rem',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {EVIDENCE_LABEL[account.proposal.evidence] || 'Förslag'}
                            </span>
                        )}
                    </div>

                    <VatDecisionControls
                        account={{ ...account, period: state.period }}
                        reviewContext={{ vatPeriodId }}
                        busy={busyAccount === account.account_id}
                        fieldOptions={fieldOptions}
                        shortLabels={shortLabels}
                        onAccept={(box) => resolve(account, { vat_box: box }, `${account.account_number} kopplat till fält ${box}.`)}
                        onChoose={(box) => resolve(account, { vat_box: box }, `${account.account_number} kopplat till fält ${box}.`)}
                        onExclude={() => resolve(account, { exclude: true }, `${account.account_number} ingår inte i momsdeklarationen.`)}
                        onClear={() => resolve(account, { clear_mapping: true }, `${account.account_number}: kopplingen borttagen och bokföringen kontrollerad igen.`)}
                    />
                </div>
            ))}
            {state.automatic?.length > 0 && <details aria-label="Automatiska momsbedömningar" className="mt-3 p-2 bg-light rounded">
                <summary>Automatisk bedömning och separat redovisning ({state.automatic.length})</summary>
                {state.automatic.map((account) => <div key={account.account_id} className="mt-2">
                    <span className="small fw-semibold">{account.account_number} {account.account_name}</span>
                    <VatTreatmentExplanation treatment={account.treatment} period={state.period} />
                    <VatAskAILink accountId={account.account_id} accountNumber={account.account_number} vatPeriodId={vatPeriodId} />
                </div>)}
            </details>}
        </div>
    );
}
